import { apiClient, handleApiResponse } from './client';
import { ApiResponse, User } from '../types';

export interface LoginRequest {
  username: string;
  password: string;
  mfaToken?: string;
  rememberMe?: boolean;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirm {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface PasswordChangeRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  newUsername?: string;
}

export const authApi = {
  /**
   * Login with username/password and optional MFA
   */
  login: async (credentials: LoginRequest): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/auth/login', credentials);
    return handleApiResponse(response);
  },

  /**
   * Register new user
   */
  register: async (userData: RegisterRequest): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/auth/register', userData);
    return handleApiResponse(response);
  },

  /**
   * Get current user information
   */
  getCurrentUser: async (): Promise<ApiResponse<{ user: User }>> => {
    const response = await apiClient.get<ApiResponse<{ user: User }>>('/auth/me');
    return handleApiResponse(response);
  },

  /**
   * Logout and revoke session
   */
  logout: async (): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/auth/logout');
    return handleApiResponse(response);
  },

  /**
   * Request password reset
   */
  requestPasswordReset: async (email: string): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/auth/password-reset', { email });
    return handleApiResponse(response);
  },

  /**
   * Confirm password reset with token
   */
  confirmPasswordReset: async (data: PasswordResetConfirm): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/auth/password-reset/confirm', data);
    return handleApiResponse(response);
  },

  /**
   * Change password (authenticated user)
   */
  changePassword: async (data: PasswordChangeRequest): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/auth/password-change', data);
    return handleApiResponse(response);
  },

  /**
   * Get password policy requirements
   */
  getPasswordPolicy: async (): Promise<ApiResponse<{ requirements: string[] }>> => {
    const response = await apiClient.get<ApiResponse<{ requirements: string[] }>>('/auth/password-policy');
    return handleApiResponse(response);
  },

  /**
   * Setup MFA for current user
   */
  setupMFA: async (): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/auth/mfa/setup');
    return handleApiResponse(response);
  },

  /**
   * Enable MFA after setup verification
   */
  enableMFA: async (token: string): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/auth/mfa/enable', { token });
    return handleApiResponse(response);
  },

  /**
   * Disable MFA
   */
  disableMFA: async (token: string): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/auth/mfa/disable', { token });
    return handleApiResponse(response);
  },

  /**
   * Resend email MFA code
   */
  resendMFACode: async (username: string): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/auth/resend-mfa', { username });
    return handleApiResponse(response);
  },
};